import React, { useState, useEffect, useMemo } from 'react';
import { MenuItem, AddOnOption, CustomizationOption, ProductSizeOption, SelectedCustomOption } from '../../../types';
import { useContent } from '../../../context/ContentContext';
import {
  DEFAULT_SIZES,
  DEFAULT_MASTER_TOPPINGS,
  DEFAULT_MASTER_SYRUPS,
  DEFAULT_SIZE,
  DEFAULT_TOPPING,
  DEFAULT_SYRUP,
  calculateItemUnitPrice,
} from '../../../data/addOnsData';
import { formatRupiah } from '../../../utils/formatters';
import { resolveMediaUrl } from '../../../utils/api';
import { X, Plus, Minus, Coffee, Sparkles, MessageSquare, Layers, Droplets, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { isMenuItemAvailableForOutlet } from '../../../utils/supabaseStock';

interface ProductAddOnsModalProps {
  product: MenuItem | null;
  outletId?: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    product: MenuItem,
    size: AddOnOption,
    topping: AddOnOption,
    syrup: AddOnOption,
    quantity: number,
    note?: string,
    customOptions?: SelectedCustomOption[]
  ) => void;
  initialSize?: AddOnOption;
  initialTopping?: AddOnOption;
  initialSyrup?: AddOnOption;
  initialCustomOptions?: SelectedCustomOption[];
  initialQuantity?: number;
  initialNote?: string;
}

export const ProductAddOnsModal: React.FC<ProductAddOnsModalProps> = ({
  product,
  outletId,
  isOpen,
  onClose,
  onConfirm,
  initialSize,
  initialTopping,
  initialSyrup,
  initialCustomOptions,
  initialQuantity = 1,
  initialNote = '',
}) => {
  const { data } = useContent();

  const isAvailableInOutlet = isMenuItemAvailableForOutlet(product, outletId);

  // Dynamic Master Toppings from Context/DB
  const masterToppings: CustomizationOption[] = useMemo(() => {
    if (Array.isArray(data.masterToppings) && data.masterToppings.length > 0) {
      return data.masterToppings.filter((t) => t.isActive);
    }
    return DEFAULT_MASTER_TOPPINGS.filter((t) => t.isActive);
  }, [data.masterToppings]);

  // Dynamic Master Syrups from Context/DB
  const masterSyrups: CustomizationOption[] = useMemo(() => {
    if (Array.isArray(data.masterSyrups) && data.masterSyrups.length > 0) {
      return data.masterSyrups.filter((s) => s.isActive);
    }
    return DEFAULT_MASTER_SYRUPS.filter((s) => s.isActive);
  }, [data.masterSyrups]);

  // Sizes available for this specific product (prioritize product-specific configured sizes)
  const availableSizes: ProductSizeOption[] = useMemo(() => {
    if (!product || product.hasSize === false) return [];

    if (Array.isArray(product.sizes) && product.sizes.length > 0) {
      const master = Array.isArray(data.masterSizes) && data.masterSizes.length > 0 ? data.masterSizes : DEFAULT_SIZES;
      const isStandardDefaultSizes = product.sizes.length === 2 &&
        product.sizes.some(s => s.name.toLowerCase() === "regular") &&
        product.sizes.some(s => s.name.toLowerCase() === "large");
      if (isStandardDefaultSizes && master.length > product.sizes.length) {
        return master;
      }
      return product.sizes;
    }

    let sizesList = Array.isArray(data.masterSizes) && data.masterSizes.length > 0
      ? data.masterSizes
      : DEFAULT_SIZES;
    if (!sizesList.some(s => s.name.trim().toLowerCase() === 'hot')) {
      sizesList = [...sizesList, { name: 'Hot', price: 0 }];
    }
    return sizesList;
  }, [product, data.masterSizes]);

  // Toppings available for this specific product
  const availableToppings: AddOnOption[] = useMemo(() => {
    if (!product || product.hasTopping === false) return [];
    let list = masterToppings;
    if (Array.isArray(product.availableToppingIds) && product.availableToppingIds.length > 0) {
      list = masterToppings.filter(
        (t) => t.id === 'top-no-topping' || product.availableToppingIds?.includes(t.id)
      );
    }
    return list.map((t) => ({ id: t.id, name: t.name, price: t.price }));
  }, [product, masterToppings]);

  // Syrups available for this specific product
  const availableSyrups: AddOnOption[] = useMemo(() => {
    if (!product || product.hasSyrup === false) return [];
    let list = masterSyrups;
    if (Array.isArray(product.availableSyrupIds) && product.availableSyrupIds.length > 0) {
      list = masterSyrups.filter(
        (s) => s.id === 'syr-no-syrup' || product.availableSyrupIds?.includes(s.id)
      );
    }
    return list.map((s) => ({ id: s.id, name: s.name, price: s.price }));
  }, [product, masterSyrups]);

  // Dynamic Customization Groups enabled for this specific product
  const activeCustomGroups = useMemo(() => {
    if (!product || !Array.isArray(data.customizationGroups)) return [];
    return data.customizationGroups
      .filter((group) => {
        // Must have options
        if (!group.options || group.options.length === 0) return false;
        // Only show if explicitly enabled for this product in product.customizations
        if (!Array.isArray(product.customizations)) return false;
        const setting = product.customizations.find((c) => c.groupId === group.id);
        return setting ? setting.enabled === true : false;
      })
      .map((group) => {
        // Filter: only show ACTIVE options, and sort them by order
        const activeOpts = (group.options || [])
          .filter((o) => o.isActive !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        return {
          ...group,
          options: activeOpts,
        };
      })
      .filter((group) => group.options.length > 0) // Only show groups that have at least one active option
      .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort customization groups by order
  }, [product, data.customizationGroups]);

  const hasSizeOption = product?.hasSize !== false && availableSizes.length > 0;
  const hasToppingOption = product?.hasTopping !== false && availableToppings.length > 0;
  const hasSyrupOption = product?.hasSyrup !== false && availableSyrups.length > 0;

  // Selected state
  const [selectedSize, setSelectedSize] = useState<AddOnOption>(DEFAULT_SIZE);
  const [selectedTopping, setSelectedTopping] = useState<AddOnOption>(DEFAULT_TOPPING);
  const [selectedSyrup, setSelectedSyrup] = useState<AddOnOption>(DEFAULT_SYRUP);
  const [selectedCustoms, setSelectedCustoms] = useState<Record<string, SelectedCustomOption>>({});
  const [quantity, setQuantity] = useState<number>(1);
  const [note, setNote] = useState<string>('');

  // Reset or load initial values whenever product opens
  useEffect(() => {
    if (isOpen && product) {
      // 1. Size
      if (initialSize) {
        const STANDARD_SIZE_ALIASES = ['regular', 'reguler', 'standard', 'standar', 'normal'];
        const matched = availableSizes.find(
          (s) => s.name.trim().toLowerCase() === initialSize.name.trim().toLowerCase()
        ) || (
          STANDARD_SIZE_ALIASES.includes(initialSize.name.trim().toLowerCase())
            ? availableSizes.find((s) => STANDARD_SIZE_ALIASES.includes(s.name.trim().toLowerCase()) || Number(s.price) === 0)
            : undefined
        );
        setSelectedSize(matched ? { name: matched.name, price: matched.price } : initialSize);
      } else if (availableSizes.length > 0) {
        setSelectedSize({ name: availableSizes[0].name, price: availableSizes[0].price });
      } else {
        setSelectedSize(DEFAULT_SIZE);
      }

      // 2. Topping
      if (initialTopping) {
        setSelectedTopping(initialTopping);
      } else {
        const noTop = availableToppings.find((t) => (t?.name || '').toLowerCase().includes('no topping'));
        setSelectedTopping(noTop || availableToppings[0] || DEFAULT_TOPPING);
      }

      // 3. Syrup
      if (initialSyrup) {
        setSelectedSyrup(initialSyrup);
      } else {
        const noSyr = availableSyrups.find((s) => (s?.name || '').toLowerCase().includes('no syrup'));
        setSelectedSyrup(noSyr || availableSyrups[0] || DEFAULT_SYRUP);
      }

      // 4. Dynamic Customizations
      const initialCustomsMap: Record<string, SelectedCustomOption> = {};
      activeCustomGroups.forEach((group) => {
        const foundInitial = initialCustomOptions?.find((c) => c.groupId === group.id);
        if (foundInitial) {
          initialCustomsMap[group.id] = foundInitial;
        } else if (group.options && group.options.length > 0) {
          const defaultOpt = group.options.find((o) => o.price === 0) || group.options[0];
          initialCustomsMap[group.id] = {
            groupId: group.id,
            groupName: group.name,
            optionId: defaultOpt.id,
            optionName: defaultOpt.name,
            price: defaultOpt.price,
          };
        }
      });
      setSelectedCustoms(initialCustomsMap);

      setQuantity(initialQuantity > 0 ? initialQuantity : 1);
      setNote(initialNote || '');
    }
  }, [
    isOpen,
    product,
    initialSize,
    initialTopping,
    initialSyrup,
    initialCustomOptions,
    initialQuantity,
    initialNote,
    availableSizes,
    availableToppings,
    availableSyrups,
    activeCustomGroups,
  ]);

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    const s = hasSizeOption ? selectedSize : { name: 'Regular', price: 0 };
    const t = hasToppingOption ? selectedTopping : { name: 'No Topping', price: 0 };
    const sy = hasSyrupOption ? selectedSyrup : { name: 'No Syrup', price: 0 };
    const customs = Object.values(selectedCustoms);
    return calculateItemUnitPrice(product.price, s, t, sy, customs);
  }, [
    product,
    hasSizeOption,
    selectedSize,
    hasToppingOption,
    selectedTopping,
    hasSyrupOption,
    selectedSyrup,
    selectedCustoms,
  ]);

  const totalPrice = useMemo(() => {
    return unitPrice * quantity;
  }, [unitPrice, quantity]);

  if (!isOpen || !product) return null;

  const handleConfirm = () => {
    const finalSize = hasSizeOption ? selectedSize : { name: 'Regular', price: 0 };
    const finalTopping = hasToppingOption ? selectedTopping : { name: 'No Topping', price: 0 };
    const finalSyrup = hasSyrupOption ? selectedSyrup : { name: 'No Syrup', price: 0 };
    
    // Validate each customization option selection against actual active groups & active options
    const finalCustoms = Object.values(selectedCustoms)
      .filter((custom) => {
        const matchingGroup = activeCustomGroups.find((g) => g.id === custom.groupId);
        if (!matchingGroup) return false;
        const matchingOption = matchingGroup.options.find((o) => o.id === custom.optionId);
        return matchingOption ? true : false;
      })
      .map((custom) => {
        const matchingGroup = activeCustomGroups.find((g) => g.id === custom.groupId)!;
        const matchingOption = matchingGroup.options.find((o) => o.id === custom.optionId)!;
        return {
          ...custom,
          optionName: matchingOption.name,
          price: matchingOption.price,
        };
      });

    onConfirm(
      product,
      finalSize,
      finalTopping,
      finalSyrup,
      quantity,
      note.trim() || undefined,
      finalCustoms
    );
    onClose();
  };

  const fallbackProductImg =
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_28801cb2ec_d849a6f1bc98f51c.png';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#0A192F]/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.96 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="relative w-full max-w-md sm:max-w-lg bg-white sm:rounded-3xl rounded-t-[26px] max-h-[82vh] sm:max-h-[85vh] flex flex-col shadow-2xl overflow-hidden text-[#0A192F]"
          role="dialog"
          aria-modal="true"
        >
          {/* Scrollable Container including Top Image Header */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {/* Top Image Header — Compact Mobile Proportion (~18-20% shorter) */}
            <div className="relative h-52 sm:h-60 bg-[#F8FAFC]">
              <img
                src={resolveMediaUrl(product.image) || fallbackProductImg}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (target.src !== fallbackProductImg) {
                    target.src = fallbackProductImg;
                  }
                }}
              />
              <div className="absolute top-3 left-3 right-3 flex justify-between items-center">
                <button
                  type="button"
                  onClick={onClose}
                  id="close-addon-modal-btn"
                  aria-label="Tutup"
                  className="w-9 h-9 bg-white/85 backdrop-blur-md rounded-full flex items-center justify-center text-[#0A192F] shadow-sm cursor-pointer hover:bg-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="w-9 h-9 bg-white/85 backdrop-blur-md rounded-full flex items-center justify-center text-[#0056D2] shadow-sm">
                  <Coffee className="w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="px-3.5 sm:px-4 pt-4 pb-4 -mt-5 bg-white rounded-t-[24px] relative z-10 space-y-3.5">
              <div>
                <div className="flex justify-between items-start mb-1.5 gap-2.5">
                  <div>
                    <span className="text-[9px] font-bold text-[#0056D2] uppercase tracking-widest block">
                      {product.badge || 'Signature Series'}
                    </span>
                    <h2 className="text-lg sm:text-xl font-bold mt-0.5 text-[#0A192F] leading-snug">
                      {product.name}
                    </h2>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-base sm:text-lg font-bold text-[#0A192F]">
                      {formatRupiah(product.price)}
                    </p>
                  </div>
                </div>

                <p className="text-[11px] text-slate-500 leading-normal">
                  {product.description ||
                    'Our signature double shot espresso poured over cold fresh milk and topped with a thick, velvety layer of savory sea salt cream foam.'}
                </p>
              </div>

              {/* A. SIZE CUP SECTION */}
              {hasSizeOption && (
                <div>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                    Size Cup
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {availableSizes.map((sizeOpt) => {
                      const isSelected = selectedSize.name.toLowerCase() === sizeOpt.name.toLowerCase() || (
                        ['regular', 'reguler', 'standard', 'standar', 'normal'].includes(selectedSize.name?.toLowerCase()) &&
                        ['regular', 'reguler', 'standard', 'standar', 'normal'].includes(sizeOpt.name?.toLowerCase())
                      );
                      return (
                        <button
                          key={sizeOpt.name}
                          type="button"
                          onClick={() =>
                            setSelectedSize({ name: sizeOpt.name, price: sizeOpt.price })
                          }
                          className={`px-3 py-2 rounded-xl text-left transition-all flex items-center justify-between gap-2 cursor-pointer ${
                            isSelected
                              ? 'border-2 border-[#0056D2] bg-blue-50/50 text-[#0056D2]'
                              : 'border border-[#E2E8F0] bg-white text-slate-600 hover:border-[#0056D2]/40'
                          }`}
                        >
                          <span className="text-[11px] font-bold truncate">{sizeOpt.name}</span>
                          <span
                            className={`text-[11px] font-semibold whitespace-nowrap ${
                              isSelected ? 'text-[#0056D2]' : 'text-slate-400'
                            }`}
                          >
                            {sizeOpt.price === 0 ? 'Free' : `+${formatRupiah(sizeOpt.price)}`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* B. TOPPING / ADD-ONS SECTION */}
              {hasToppingOption && (
                <div>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                    Topping / Add-Ons
                  </h3>
                  <div className="space-y-1.5">
                    {availableToppings.map((opt) => {
                      const isSelected = selectedTopping.name === opt.name;
                      return (
                        <button
                          key={opt.name}
                          type="button"
                          onClick={() => setSelectedTopping(opt)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all cursor-pointer ${
                            isSelected
                              ? 'border-2 border-[#0056D2] bg-blue-50/50 text-[#0A192F]'
                              : 'border border-[#E2E8F0] bg-white text-[#0A192F]'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                                isSelected
                                  ? 'border-[#0056D2] bg-[#0056D2]'
                                  : 'border-[#E2E8F0] bg-white'
                              }`}
                            >
                              {isSelected && (
                                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                              )}
                            </div>
                            <span className="text-[11px] font-semibold">{opt.name}</span>
                          </div>
                          <span className="text-[11px] font-bold text-slate-400">
                            {opt.price === 0 ? '+Rp 0' : `+${formatRupiah(opt.price)}`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* C. SYRUP / ADD-ONS SECTION */}
              {hasSyrupOption && (
                <div>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                    Syrup / Add-Ons
                  </h3>
                  <div className="space-y-1.5">
                    {availableSyrups.map((opt) => {
                      const isSelected = selectedSyrup.name === opt.name;
                      return (
                        <button
                          key={opt.name}
                          type="button"
                          onClick={() => setSelectedSyrup(opt)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all cursor-pointer ${
                            isSelected
                              ? 'border-2 border-[#0056D2] bg-blue-50/50 text-[#0A192F]'
                              : 'border border-[#E2E8F0] bg-white text-[#0A192F]'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                                isSelected
                                  ? 'border-[#0056D2] bg-[#0056D2]'
                                  : 'border-[#E2E8F0] bg-white'
                              }`}
                            >
                              {isSelected && (
                                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                              )}
                            </div>
                            <span className="text-[11px] font-semibold">{opt.name}</span>
                          </div>
                          <span className="text-[11px] font-bold text-slate-400">
                            {opt.price === 0 ? '+Rp 0' : `+${formatRupiah(opt.price)}`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* DYNAMIC CUSTOMIZATION GROUPS SECTIONS */}
              {activeCustomGroups
                .filter((group) => {
                  const groupName = (group?.name || '').toLowerCase();
                  if (groupName.includes('toping donat')) {
                    return product.use_topping_donut === true;
                  }
                  if (groupName.includes('toping maincourse')) {
                    return product.use_topping_maincourse === true;
                  }
                  return true;
                })
                .map((group) => {
                  const selectedOpt = selectedCustoms[group.id];

                  return (
                    <div key={group.id}>
                      <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                        {group.name}
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {group.options.map((opt) => {
                          const isSelected = selectedOpt?.optionId === opt.id;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => {
                                setSelectedCustoms((prev) => ({
                                  ...prev,
                                  [group.id]: {
                                    groupId: group.id,
                                    groupName: group.name,
                                    optionId: opt.id,
                                    optionName: opt.name,
                                    price: opt.price,
                                  },
                                }));
                              }}
                              className={`py-2 px-2.5 rounded-xl text-[11px] transition-all cursor-pointer flex flex-col items-center justify-center text-center ${
                                isSelected
                                  ? 'border-2 border-[#0056D2] bg-blue-50/50 text-[#0056D2] font-bold'
                                  : 'border border-[#E2E8F0] bg-white text-slate-500 font-semibold'
                              }`}
                            >
                              <span className="truncate max-w-full">{opt.name}</span>
                              {opt.price > 0 && (
                                <span className="text-[10px] mt-0.5 opacity-80">
                                  +{formatRupiah(opt.price)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

              {/* SPECIAL NOTE SECTION */}
              <div>
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                  Special Notes (Optional)
                </h3>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Example: Less ice, normal sugar..."
                  className="w-full px-3 py-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-base sm:text-[11px] text-[#0A192F] placeholder:text-slate-400 focus:outline-none focus:border-[#0056D2] transition-colors"
                  maxLength={120}
                />
              </div>

              {/* Stock status warning if unavailable */}
              {!isAvailableInOutlet && (
                <div className="p-2.5 rounded-xl bg-rose-50 border border-rose-200 flex items-center gap-2 text-rose-700 text-[11px] font-medium">
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>Menu ini sedang HABIS di cabang yang dipilih.</span>
                </div>
              )}
            </div>
          </div>

          {/* Sticky Bottom Action — Exact UX Pilot Footer */}
          <div
            className="bg-white border-t border-[#E2E8F0] px-3.5 py-3 sm:px-4 sm:py-3.5 flex items-center gap-3 shrink-0"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="flex items-center border border-[#E2E8F0] rounded-xl px-1.5 py-0.5 bg-[#F8FAFC]">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-slate-400 hover:text-[#0A192F] cursor-pointer"
                aria-label="Kurangi jumlah"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-7 text-center font-bold text-xs sm:text-sm text-[#0A192F]">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-[#0A192F] cursor-pointer"
                aria-label="Tambah jumlah"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <button
              type="button"
              disabled={!isAvailableInOutlet}
              onClick={handleConfirm}
              id="confirm-add-ons-btn"
              className={`flex-1 py-2.5 sm:py-3 px-4 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all ${
                isAvailableInOutlet
                  ? 'bg-[#0056D2] hover:bg-blue-700 text-white shadow-lg shadow-[#0056D2]/20 cursor-pointer active:scale-95'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <span>
                {isAvailableInOutlet
                  ? `Add to Cart • ${formatRupiah(totalPrice)}`
                  : 'Sold Out'}
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
